import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Search, Plus, X, Mail, Phone, MapPin, Crown, Save, Copy, Send, ExternalLink, CheckCircle2, Flag, Key, Archive, Edit, Wallet, Ticket, MessageSquare, Eye, BellRing, FolderInput } from "lucide-react";
import { toast } from "sonner";
import api from "./api";

/**
 * Customer Database , single searchable list, slide-over profile drawer.
 * Profile shows full info + linked orders + invoices + admin notes.
 */
export default function ConsoleCustomers() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null); // customer id
  const [profile, setProfile] = useState(null);
  const [creating, setCreating] = useState(false);
  const [hqOnly, setHqOnly] = useState("all");        // all | hq | non_hq
  const [sortBy, setSortBy] = useState("name");       // name | newest | oldest | orders
  const [groupFilter, setGroupFilter] = useState("all"); // all | __none | <group name>
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [emailModal, setEmailModal] = useState(false);
  const [loadModal, setLoadModal] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/console/customers", { params: { q } });
      setList(Array.isArray(r.data) ? r.data : []);
    } catch {
      toast.error("Couldn't load customers.");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const openProfile = async (id) => {
    setOpen(id);
    setProfile(null);
    try {
      const r = await api.get(`/console/customers/${id}`);
      setProfile(r.data);
    } catch { toast.error("Couldn't load profile."); }
  };

  const groups = useMemo(
    () => [...new Set(list.map((c) => (c.group || "").trim()).filter(Boolean))].sort(),
    [list]
  );

  const visible = useMemo(() => {
    let xs = list;
    if (hqOnly === "hq") xs = xs.filter((c) => c.in_hq);
    if (hqOnly === "non_hq") xs = xs.filter((c) => !c.in_hq);
    if (groupFilter === "__none") xs = xs.filter((c) => !(c.group || "").trim());
    else if (groupFilter !== "all") xs = xs.filter((c) => (c.group || "").trim() === groupFilter);
    const cmp = {
      name:    (a, b) => (a.name || a.email || "").localeCompare(b.name || b.email || ""),
      newest:  (a, b) => (b.created_at || "").localeCompare(a.created_at || ""),
      oldest:  (a, b) => (a.created_at || "").localeCompare(b.created_at || ""),
      orders:  (a, b) => (b.order_count || 0) - (a.order_count || 0),
    }[sortBy];
    return [...xs].sort(cmp || (() => 0));
  }, [list, hqOnly, sortBy, groupFilter]);

  const counts = useMemo(() => ({
    all: list.length,
    hq: list.filter((c) => c.in_hq).length,
    non_hq: list.filter((c) => !c.in_hq).length,
  }), [list]);

  const toggleSel = (id) => setSelected((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const allVisibleSelected = visible.length > 0 && visible.every((c) => selected.has(c.id));
  const toggleSelectAll = () => setSelected(allVisibleSelected ? new Set() : new Set(visible.map((c) => c.id)));

  const bulkGroup = async () => {
    const ids = [...selected];
    const name = window.prompt(
      `Move ${ids.length} customer${ids.length === 1 ? "" : "s"} to which folder?\n` +
      (groups.length ? `Existing: ${groups.join(", ")}\n` : "") +
      "Type a folder name (or leave blank to remove from folders):"
    );
    if (name === null) return;
    setBulkBusy(true);
    try {
      const r = await api.post("/console/customers/bulk-group", { ids, group: name.trim() });
      const g = r.data?.group || "";
      toast.success(g ? `Moved to "${g}".` : "Removed from folders.");
      setList((prev) => prev.map((c) => (selected.has(c.id) ? { ...c, group: g } : c)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't move to folder.");
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkVip = async (enabled) => {
    const ids = [...selected];
    if (!window.confirm(`Turn VIP Build-a-Box ${enabled ? "ON" : "OFF"} for ${ids.length} customer${ids.length === 1 ? "" : "s"}?`)) return;
    setBulkBusy(true);
    try {
      const r = await api.post("/console/customers/bulk-vip", { ids, enabled });
      toast.success(`VIP ${enabled ? "enabled" : "disabled"} for ${r.data?.matched ?? ids.length} customer${ids.length === 1 ? "" : "s"}.`);
      setList((prev) => prev.map((c) => (selected.has(c.id) ? { ...c, vip_enabled: enabled } : c)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk VIP update failed.");
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkInvite = async () => {
    const ids = [...selected];
    if (!window.confirm(`Email an HQ invite to ${ids.length} selected customer${ids.length === 1 ? "" : "s"}? Anyone already in HQ is skipped automatically.`)) return;
    setBulkBusy(true);
    try {
      const r = await api.post("/console/customers/bulk-invite", { ids });
      const d = r.data || {};
      toast.success(`${d.queued} invite${d.queued === 1 ? "" : "s"} sending now · ${d.already_in_hq} already in HQ${d.no_email ? ` · ${d.no_email} missing an email` : ""}`);
      setList((prev) => prev.map((c) => (selected.has(c.id) && !c.in_hq && c.email ? { ...c, invite_sent: true } : c)));
      setSelected(new Set());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk invite failed.");
    } finally { setBulkBusy(false); }
  };

  return (
    <div data-testid="console-customers">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#6E1E3A] font-semibold">Heritage Dahlias · Console</div>
          <h1 className="font-serif text-4xl text-[#3C2A1E] mt-1">Customer Database</h1>
          <p className="text-[#7A6A55] mt-2 max-w-2xl text-sm">
            Every customer in the new system. Click a row for full profile, order history, invoices, credits and admin notes.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setLoadModal(true)} data-testid="customer-mass-load"
            className="px-4 py-2 bg-white border border-[#6E1E3A] hover:bg-[#FBF6E9] text-[#6E1E3A] rounded text-xs tracking-[0.2em] uppercase font-semibold inline-flex items-center gap-1.5"
            title="Paste a list of names + emails to load customers in bulk (and optionally invite them to HQ)">
            <FolderInput className="w-3.5 h-3.5" /> Load customers
          </button>
          <button
            type="button"
            onClick={async () => {
              try {
                const r = await api.get("/console/customers/invite-links-bulk", { params: { only_non_hq: true } });
                const rows = r.data?.rows || [];
                if (!rows.length) { toast.info("No customers with emails to invite yet."); return; }
                const lines = rows.map((x) => `${x.name || "(no name)"}, ${x.email}, ${x.signup_url}`);
                const text = `Heritage Headquarters invite links (${rows.length} customer${rows.length === 1 ? "" : "s"})\n\n${lines.join("\n")}\n`;
                await navigator.clipboard.writeText(text);
                toast.success(`Copied ${rows.length} signup link${rows.length === 1 ? "" : "s"} to clipboard.`);
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Couldn't build invite list.");
              }
            }}
            data-testid="customer-copy-invite-links"
            className="px-4 py-2 bg-white border border-[#6E1E3A] hover:bg-[#FBF6E9] text-[#6E1E3A] rounded text-xs tracking-[0.2em] uppercase font-semibold inline-flex items-center gap-1.5"
            title="Copy every non-HQ customer's signup URL for a mail-merge paste"
          >
            <Copy className="w-3.5 h-3.5" /> Copy invite links
          </button>
          <button type="button" onClick={() => setCreating(true)} data-testid="customer-new" className="px-4 py-2 bg-[#6E1E3A] hover:bg-[#5a1830] text-white rounded text-xs tracking-[0.2em] uppercase font-semibold inline-flex items-center gap-1.5">
            <Plus className="w-3.5 h-3.5" /> Add customer
          </button>
        </div>
      </header>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 px-3 py-2 bg-white border border-[#E8DFCB] rounded flex-1 min-w-[260px] max-w-md">
          <Search className="w-4 h-4 text-[#9C8B73]" />
          <input type="text" placeholder="Search name, email, phone…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} data-testid="customer-search" className="flex-1 outline-none text-sm bg-transparent" />
          <button type="button" onClick={load} className="text-[11px] text-[#6E1E3A] hover:underline">Search</button>
        </div>
        <div className="flex items-center gap-1 bg-white border border-[#E8DFCB] rounded p-0.5" role="tablist" aria-label="Filter by HQ">
          {[
            { v: "all",    l: `All (${counts.all})` },
            { v: "hq",     l: `HQ (${counts.hq})` },
            { v: "non_hq", l: `Non-HQ (${counts.non_hq})` },
          ].map((opt) => (
            <button
              key={opt.v} type="button"
              onClick={() => setHqOnly(opt.v)}
              data-testid={`customer-filter-${opt.v}`}
              className={`px-3 py-1.5 text-[10px] tracking-[0.25em] uppercase font-semibold rounded ${
                hqOnly === opt.v ? "bg-[#6E1E3A] text-white" : "text-[#3C2A1E] hover:bg-[#FBF6E9]"
              }`}
            >
              {opt.l}
            </button>
          ))}
        </div>
        <label className="inline-flex items-center gap-2 bg-white border border-[#E8DFCB] rounded px-2.5 py-1.5">
          <span className="text-[10px] tracking-[0.25em] uppercase text-[#9C8B73]">Folder</span>
          <select value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)} data-testid="customer-group-filter" className="text-xs bg-transparent outline-none max-w-[140px]">
            <option value="all">All folders</option>
            <option value="__none">No folder</option>
            {groups.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
        </label>
        <label className="inline-flex items-center gap-2 bg-white border border-[#E8DFCB] rounded px-2.5 py-1.5">
          <span className="text-[10px] tracking-[0.25em] uppercase text-[#9C8B73]">Sort</span>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} data-testid="customer-sort" className="text-xs bg-transparent outline-none">
            <option value="name">Name A-Z</option>
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="orders">Most orders</option>
          </select>
        </label>
      </div>

      {selected.size > 0 && (
        <div data-testid="bulk-actions-bar" className="mb-4 flex flex-wrap items-center gap-2 bg-[#3E1F46] text-[#F4ECD8] rounded px-3 py-2">
          <span className="text-xs font-semibold mr-1">{selected.size} selected</span>
          <button type="button" disabled={bulkBusy} onClick={() => bulkVip(true)} data-testid="bulk-vip-on"
            className="px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase font-semibold rounded bg-[#C9A268] text-[#3E1F46] hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1.5">
            <Crown className="w-3 h-3" /> VIP on
          </button>
          <button type="button" disabled={bulkBusy} onClick={() => bulkVip(false)} data-testid="bulk-vip-off"
            className="px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase font-semibold rounded border border-[#C9A268] text-[#F4ECD8] hover:bg-white/10 disabled:opacity-50 inline-flex items-center gap-1.5">
            <Crown className="w-3 h-3" /> VIP off
          </button>
          <button type="button" disabled={bulkBusy} onClick={bulkGroup} data-testid="bulk-group"
            className="px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase font-semibold rounded border border-[#F4ECD8]/60 text-[#F4ECD8] hover:bg-white/10 disabled:opacity-50 inline-flex items-center gap-1.5">
            <FolderInput className="w-3 h-3" /> Folder…
          </button>
          <button type="button" disabled={bulkBusy} onClick={() => setEmailModal(true)} data-testid="bulk-email-open"
            className="px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase font-semibold rounded bg-[#F4ECD8] text-[#3E1F46] hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1.5">
            <Mail className="w-3 h-3" /> Send email…
          </button>
          <button type="button" disabled={bulkBusy} onClick={bulkInvite} data-testid="bulk-invite-hq"
            className="px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase font-semibold rounded bg-[#C49247] text-white hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1.5">
            <Send className="w-3 h-3" /> Invite to HQ
          </button>
          <button type="button" onClick={() => setSelected(new Set())} data-testid="bulk-clear"
            className="ml-auto px-2 py-1.5 text-[10px] tracking-[0.2em] uppercase font-semibold text-[#F4ECD8]/75 hover:text-white inline-flex items-center gap-1">
            <X className="w-3 h-3" /> Clear
          </button>
        </div>
      )}

      {loading ? (
        <div className="text-[#7A6A55] inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : visible.length === 0 ? (
        <div className="bg-white border border-dashed border-[#E8DFCB] p-10 text-center text-[#7A6A55] rounded">
          No customers yet. Add one above, or create one when you build your first order.
        </div>
      ) : (
        <div className="bg-white border border-[#E8DFCB] rounded-md divide-y divide-[#F4ECD8]">
          <label className="flex items-center gap-2 px-3 py-2 cursor-pointer select-none bg-[#FBF6E9]/60">
            <input
              type="checkbox"
              checked={allVisibleSelected}
              onChange={toggleSelectAll}
              data-testid="select-all-customers"
              className="w-4 h-4 accent-[#6E1E3A]"
            />
            <span className="text-[10px] tracking-[0.25em] uppercase font-semibold text-[#7A6A55]">
              Select all shown ({visible.length})
            </span>
          </label>
          {visible.map((c) => (
            <div key={c.id} className="flex items-stretch">
              <label className="flex items-center pl-3 pr-1 cursor-pointer" onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={selected.has(c.id)}
                  onChange={() => toggleSel(c.id)}
                  data-testid={`select-customer-${c.id}`}
                  className="w-4 h-4 accent-[#6E1E3A]"
                />
              </label>
              <button type="button" onClick={() => openProfile(c.id)} data-testid={`customer-row-${c.id}`} className="flex-1 min-w-0 text-left p-3 hover:bg-[#FBF6E9] grid grid-cols-1 md:grid-cols-12 gap-3 items-start">
                <div className="md:col-span-4">
                  <div className="font-serif text-base text-[#3C2A1E]">{c.name || c.email || "(no name)"}</div>
                  {c.email && <div className="text-xs text-[#7A6A55] inline-flex items-center gap-1"><Mail className="w-3 h-3" />{c.email}</div>}
                </div>
                <div className="md:col-span-3 text-xs text-[#7A6A55]">
                  {c.phone && <div className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</div>}
                </div>
                <div className="md:col-span-4 text-xs text-[#7A6A55]">
                  {c.address && <div className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{c.address}</div>}
                </div>
                <div className="md:col-span-1 text-right space-y-1">
                  {(c.group || "").trim() && (
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 bg-[#E8DFCB] text-[#5B2A4F] rounded inline-block max-w-[90px] truncate" title={c.group}>
                      {c.group}
                    </span>
                  )}
                  {c.invite_sent && !c.in_hq && (
                    <span className="text-[10px] tracking-[0.15em] uppercase px-1.5 py-0.5 bg-[#C49247] text-white rounded inline-block">invited</span>
                  )}
                  {Number(c.unread_messages || 0) > 0 && (
                    <span data-testid={`unread-badge-${c.id}`} className="text-[10px] font-semibold px-1.5 py-0.5 bg-[#A33030] text-white rounded inline-flex items-center gap-1">
                      <MessageSquare className="w-3 h-3" /> {c.unread_messages}
                    </span>
                  )}
                  {c.in_hq && <span className="text-[10px] tracking-[0.25em] uppercase px-1.5 py-0.5 bg-[#6E1E3A] text-white rounded inline-flex items-center gap-1"><Crown className="w-3 h-3" /> HQ</span>}
                </div>
              </button>
            </div>
          ))}
        </div>
      )}

      {open && (
        <ProfileDrawer
          profile={profile}
          onClose={() => { setOpen(null); setProfile(null); }}
          onSaved={(c) => {
            setList((prev) => prev.map((x) => x.id === c.id ? c : x));
            setProfile((p) => p ? { ...p, customer: c } : p);
          }}
        />
      )}
      {creating && (
        <NewCustomerDrawer
          onClose={() => setCreating(false)}
          onCreated={(c) => { setList((prev) => [c, ...prev]); setCreating(false); }}
        />
      )}
      {emailModal && (
        <BulkEmailModal
          ids={[...selected]}
          onClose={() => setEmailModal(false)}
        />
      )}
      {loadModal && (
        <MassLoadModal
          onClose={() => setLoadModal(false)}
          onDone={() => { setLoadModal(false); load(); }}
        />
      )}
    </div>
  );
}

function MassLoadModal({ onClose, onDone }) {
  const [text, setText] = useState("");
  const [sendInvites, setSendInvites] = useState(true);
  const [busy, setBusy] = useState(false);

  const rows = useMemo(() => text.split(/\n/).map((l) => l.trim()).filter(Boolean).map((l) => {
    const m = l.match(/[\w.+-]+@[\w-]+(\.[\w-]+)+/);
    if (!m) return null;
    return { email: m[0].toLowerCase(), name: l.replace(m[0], "").replace(/[,;\t"]+/g, " ").replace(/\s+/g, " ").trim() };
  }).filter(Boolean), [text]);

  const submit = async () => {
    if (rows.length === 0) { toast.error("No valid emails found in the paste."); return; }
    setBusy(true);
    try {
      const r = await api.post("/console/customers/load", { rows, send_invites: sendInvites });
      const d = r.data || {};
      toast.success(`Loaded: ${d.created} new · ${d.updated} updated${d.invalid ? ` · ${d.invalid} skipped` : ""}${sendInvites ? ` · ${d.invites_queued} HQ invite${d.invites_queued === 1 ? "" : "s"} sending` : ""}`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't load customers."); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="mass-load-modal">
      <div className="bg-[#FBF6E9] rounded-lg shadow-xl w-full max-w-xl p-6">
        <div className="flex items-start justify-between mb-1">
          <h2 className="font-serif text-2xl text-[#3C2A1E]">Load customers in bulk</h2>
          <button type="button" onClick={onClose} data-testid="mass-load-close" className="p-1 text-[#7A6A55] hover:text-[#3C2A1E]"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-xs text-[#7A6A55] mb-3">Paste one customer per line , name and email in any order (commas, tabs or spaces all work). Existing customers are updated, new ones created.</p>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={9} data-testid="mass-load-textarea"
          placeholder={"Jane Bloom, jane@example.nz\nrob@example.nz\tRob Petal\nkiri@example.nz"}
          className="w-full border rounded p-3 text-sm bg-white outline-none font-mono" style={{ borderColor: "#E8DFCB" }} />
        <div className="flex items-center justify-between mt-3 flex-wrap gap-3">
          <label className="inline-flex items-center gap-2 text-xs font-semibold text-[#3C2A1E] cursor-pointer">
            <input type="checkbox" checked={sendInvites} onChange={(e) => setSendInvites(e.target.checked)} data-testid="mass-load-send-invites" />
            Also email each new person an HQ signup invite
          </label>
          <div className="flex items-center gap-2">
            <span className="text-xs text-[#7A6A55]">{rows.length} valid row{rows.length === 1 ? "" : "s"}</span>
            <button type="button" onClick={submit} disabled={busy || rows.length === 0} data-testid="mass-load-submit"
              className="px-5 py-2 bg-[#6E1E3A] hover:bg-[#5a1830] text-white rounded text-xs tracking-[0.2em] uppercase font-semibold inline-flex items-center gap-1.5 disabled:opacity-50">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Load{sendInvites ? " + invite" : ""}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function BulkEmailModal({ ids, onClose }) {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!subject.trim() || !message.trim()) {
      toast.error("Subject and message are both required.");
      return;
    }
    if (!window.confirm(`Send this email to ${ids.length} customer${ids.length === 1 ? "" : "s"}?`)) return;
    setSending(true);
    try {
      const r = await api.post("/console/customers/bulk-email", { ids, subject, message });
      const d = r.data || {};
      let msg = `Sent ${d.sent ?? 0} email${(d.sent ?? 0) === 1 ? "" : "s"}.`;
      if (d.skipped_no_email) msg += ` ${d.skipped_no_email} skipped (no email / duplicate).`;
      if (d.failed?.length) msg += ` ${d.failed.length} failed.`;
      (d.failed?.length ? toast.warning : toast.success)(msg);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="bulk-email-modal">
      <div className="bg-[#FBF6E9] border border-[#E0D1C8] rounded-md w-full max-w-lg p-5 shadow-xl">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <div className="text-[10px] tracking-[0.35em] uppercase font-semibold text-[#5B2A4F]">✦ Mass email</div>
            <h2 className="font-serif text-2xl text-[#3E1F46] mt-0.5">
              Email {ids.length} customer{ids.length === 1 ? "" : "s"}
            </h2>
          </div>
          <button type="button" onClick={onClose} data-testid="bulk-email-close" className="text-[#7A6A55] hover:text-[#3E1F46]"><X className="w-5 h-5" /></button>
        </div>
        <label className="block mb-3">
          <span className="text-[10px] tracking-[0.3em] uppercase text-[#7A6A55] font-semibold">Subject</span>
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            data-testid="bulk-email-subject"
            className="mt-1 w-full bg-white border border-[#E0D1C8] rounded px-3 py-2 text-sm outline-none focus:border-[#6E1E3A]"
            placeholder="A note from Heritage Dahlias"
          />
        </label>
        <label className="block mb-1">
          <span className="text-[10px] tracking-[0.3em] uppercase text-[#7A6A55] font-semibold">Message</span>
          <textarea
            rows={7}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            data-testid="bulk-email-message"
            className="mt-1 w-full bg-white border border-[#E0D1C8] rounded px-3 py-2 text-sm outline-none focus:border-[#6E1E3A]"
            placeholder={"Kia ora {name},\n\nWrite your message here…"}
          />
        </label>
        <p className="text-[11px] text-[#7A6A55] mb-4">
          Tip: <code className="bg-white border border-[#E0D1C8] rounded px-1">{"{name}"}</code> becomes each customer&apos;s name. Sent with the Heritage email design via Resend.
        </p>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 h-10 text-xs tracking-[0.2em] uppercase font-semibold rounded border border-[#E0D1C8] text-[#3C2A1E] hover:bg-white">
            Cancel
          </button>
          <button
            type="button"
            onClick={send}
            disabled={sending}
            data-testid="bulk-email-send"
            className="px-4 h-10 text-xs tracking-[0.2em] uppercase font-semibold rounded bg-[#6E1E3A] hover:bg-[#5a1830] text-white inline-flex items-center gap-2 disabled:opacity-60"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Send to {ids.length}
          </button>
        </div>
      </div>
    </div>
  );
}

function ProfileDrawer({ profile, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [hqLegacy, setHqLegacy] = useState(null);
  const [rebuildBusy, setRebuildBusy] = useState("");
  const [invite, setInvite] = useState(null);
  const [genBusy, setGenBusy] = useState(false);
  const [accessCode, setAccessCode] = useState(null);
  const [loginLink, setLoginLink] = useState(null);
  const [linkBusy, setLinkBusy] = useState(false);

  const sendLoginLink = async () => {
    setLinkBusy(true);
    try {
      const r = await api.post(`/admin/customers/${form.id}/login-link`);
      setLoginLink(r.data);
      toast.success(r.data.emailed ? "Login link emailed , copy is below too." : "Login link ready , copy it across.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't make a login link.");
    } finally {
      setLinkBusy(false);
    }
  };

  const generateAccess = async () => {
    if (!form?.email) return;
    if (!window.confirm(`Generate a new login code for ${form.name || form.email}?\n\nIf they already have an account, their current password will be replaced.`)) return;
    setGenBusy(true);
    try {
      const r = await api.post(`/console/customers/${form.id}/generate-access`);
      setAccessCode(r.data);
      toast.success("Login code ready , copy it and send it however you like.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't generate a code.");
    } finally {
      setGenBusy(false);
    }
  };

  useEffect(() => {
    if (profile?.customer) {
      setForm({ ...profile.customer });
      loadInvite(profile.customer.id);
      if (profile.customer.in_hq) loadHqLegacy(profile.customer.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  const loadHqLegacy = async (id) => {
    try {
      const r = await api.get(`/console/customers/${id}/existing-hq-orders`);
      setHqLegacy(r.data);
    } catch { /* soft */ }
  };

  const loadInvite = async (id) => {
    try {
      const r = await api.get(`/console/customers/${id}/invite-link`);
      setInvite(r.data);
    } catch { /* soft */ }
  };

  const rebuildOrder = async (source, source_id, label) => {
    if (!window.confirm(`Copy "${label}" into a brand new console order (stage: Assigned)?\nThe original HQ record stays untouched.`)) return;
    setRebuildBusy(source_id);
    try {
      const r = await api.post(`/console/customers/${form.id}/rebuild-order`, { source, source_id });
      toast.success(`Order #${r.data.id.slice(0,8)} created in Assigned.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't rebuild.");
    } finally { setRebuildBusy(""); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.patch(`/console/customers/${form.id}`, form);
      toast.success("Profile saved.");
      onSaved(r.data);
    } catch { toast.error("Save failed."); } finally { setBusy(false); }
  };

  const togglePriority = async () => {
    try {
      const r = await api.post(`/console/customers/${form.id}/priority`);
      setForm((f) => ({ ...f, priority: r.data.priority }));
      toast.success(r.data.priority ? "Flagged as priority." : "Priority removed.");
      onSaved({ ...form, priority: r.data.priority });
    } catch { toast.error("Couldn't update flag."); }
  };

  return (
    <div className="fixed inset-0 z-40 flex justify-end" data-testid="customer-drawer">
      <button type="button" onClick={onClose} className="absolute inset-0 bg-black/30" />
      <aside className="relative w-full max-w-md bg-[#F8F4EA] h-full overflow-y-auto p-6 shadow-2xl">
        <button type="button" onClick={onClose} className="absolute top-3 right-3 p-1.5 hover:bg-[#E8DFCB] rounded" data-testid="customer-drawer-close">
          <X className="w-4 h-4" />
        </button>
        {!form ? (
          <div className="pt-10 text-[#7A6A55] inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : (
          <>
            <div className="text-[10px] tracking-[0.35em] uppercase text-[#6E1E3A] font-semibold">Customer profile</div>
            <h2 className="font-serif text-3xl text-[#3C2A1E] mt-1">{form.name}</h2>
            <div className="text-xs text-[#7A6A55] mt-1">#{form.id?.slice(0, 8)}</div>
            <div className="mt-5 space-y-3">
              <FieldRow label="Name"><Inp v={form.name} on={(v) => setForm({ ...form, name: v })} tid="profile-name" /></FieldRow>
              <FieldRow label="Email"><Inp v={form.email} on={(v) => setForm({ ...form, email: v })} tid="profile-email" /></FieldRow>
              <FieldRow label="Phone"><Inp v={form.phone} on={(v) => setForm({ ...form, phone: v })} tid="profile-phone" /></FieldRow>
              <FieldRow label="Address"><Inp v={form.address} on={(v) => setForm({ ...form, address: v })} tid="profile-address" /></FieldRow>
              <FieldRow label="Notes (visible)"><Txt v={form.notes} on={(v) => setForm({ ...form, notes: v })} tid="profile-notes" /></FieldRow>
              <FieldRow label="Admin notes (internal)"><Txt v={form.admin_notes} on={(v) => setForm({ ...form, admin_notes: v })} tid="profile-admin-notes" /></FieldRow>
              <label className="flex items-center gap-2 text-sm text-[#3C2A1E]">
                <input type="checkbox" checked={!!form.invite_sent} onChange={(e) => setForm({ ...form, invite_sent: e.target.checked })} data-testid="profile-invite-sent" /> HQ invite sent
              </label>
              <label className="flex items-center gap-2 text-sm text-[#3C2A1E]">
                <input type="checkbox" checked={!!form.in_hq} onChange={(e) => setForm({ ...form, in_hq: e.target.checked })} data-testid="profile-hq" /> In Heritage HQ <span className="text-[11px] text-[#9C8B73]">(ticks itself when they create their account)</span>
              </label>
              <FieldRow label="Folder"><Inp v={form.group || ""} on={(v) => setForm({ ...form, group: v })} tid="profile-group" /></FieldRow>
            </div>
            <div className="mt-5 flex gap-2">
              <button type="button" onClick={save} disabled={busy} data-testid="profile-save" className="flex-1 px-4 py-2.5 bg-[#3C2A1E] hover:bg-[#26190E] text-white rounded text-xs tracking-[0.25em] uppercase font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
              </button>
              <button
                type="button"
                onClick={togglePriority}
                data-testid="profile-priority-toggle"
                title={form?.priority ? "Remove priority flag" : "Flag this customer for follow-up"}
                className={"px-3 py-2.5 rounded text-xs tracking-[0.25em] uppercase font-semibold inline-flex items-center gap-1.5 transition " + (form?.priority ? "bg-[#C49247] text-white border border-[#C49247]" : "bg-white text-[#3C2A1E] border border-[#C9B89A] hover:border-[#C49247]")}
              >
                <Flag className="w-3.5 h-3.5" />
                {form?.priority ? "Priority ✓" : "Priority"}
              </button>
            </div>
            <Link
              to={`/admin/orders/create?customer_id=${encodeURIComponent(form.id || "")}&name=${encodeURIComponent(form.name || "")}&email=${encodeURIComponent(form.email || "")}&phone=${encodeURIComponent(form.phone || "")}&address=${encodeURIComponent(form.address || "")}`}
              data-testid="profile-add-order"
              className="mt-2 w-full px-4 py-2.5 bg-[#6E1E3A] hover:bg-[#5a1830] text-white rounded text-xs tracking-[0.25em] uppercase font-semibold inline-flex items-center justify-center gap-2"
            >
              <Plus className="w-3.5 h-3.5" /> Add order for {(form.name || "this customer").split(" ")[0]}
            </Link>

            {/* Manual access code , works even when email delivery is down. */}
            <div className="mt-4 bg-white border border-[#E0D1C8] rounded p-3">
              <div className="text-[10px] tracking-[0.3em] uppercase font-semibold text-[#5B2A4F] flex items-center gap-1.5">
                <Key className="w-3.5 h-3.5" /> Login code (no email)
              </div>
              <p className="text-[11px] text-[#7A6A55] mt-1">
                Generates a temporary password you can pass on yourself , nothing is emailed.
                Creates their HQ account if they don&apos;t have one, otherwise resets their password.
                They&apos;ll pick their own password on first login.
              </p>
              <button
                type="button"
                disabled={genBusy || !form.email}
                onClick={generateAccess}
                data-testid="profile-generate-access"
                className="mt-2 w-full px-3 py-2 rounded border border-[#5B2A4F] text-[#5B2A4F] hover:bg-[#5B2A4F]/5 text-xs tracking-[0.2em] uppercase font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {genBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Key className="w-3.5 h-3.5" />}
                Generate login code
              </button>
              {accessCode && (
                <div className="mt-2 bg-[#FBF6E9] border border-dashed border-[#C49247] rounded p-2.5" data-testid="profile-access-code">
                  <div className="flex items-center justify-between gap-2">
                    <code className="font-mono text-lg font-bold text-[#3E1F46] tracking-wider">{accessCode.temp_password}</code>
                    <button
                      type="button"
                      data-testid="profile-access-copy"
                      onClick={() => { navigator.clipboard.writeText(`Heritage HQ login\n${window.location.origin}/account/login\nEmail: ${accessCode.email}\nTemporary password: ${accessCode.temp_password}`); toast.success("Copied login details."); }}
                      className="shrink-0 text-[10px] tracking-[0.2em] uppercase font-semibold text-[#6E1E3A] inline-flex items-center gap-1 hover:underline"
                    >
                      <Copy className="w-3 h-3" /> Copy
                    </button>
                  </div>
                  <p className="text-[11px] text-[#7A6A55] mt-1">
                    {accessCode.created_account ? "New HQ account created." : "Password reset."} They log in at
                    {" "}<span className="font-semibold">/account/login</span> with their email + this code.
                  </p>
                </div>
              )}
            </div>

            {/* One-time login link , no password at all. */}
            <div className="mt-3 bg-white border border-[#E0D1C8] rounded p-3" data-testid="profile-login-link-panel">
              <div className="text-[10px] tracking-[0.3em] uppercase font-semibold text-[#5B2A4F] flex items-center gap-1.5">
                <Key className="w-3.5 h-3.5" /> Send login link
              </div>
              <p className="text-[11px] text-[#7A6A55] mt-1">
                One-time link straight into their HQ , no password needed. Works for 72 hours and once only.
                We try to email it and always give you the link to send yourself.
              </p>
              <button
                type="button"
                disabled={linkBusy || !form.email}
                onClick={sendLoginLink}
                data-testid="profile-send-login-link"
                className="mt-2 w-full px-3 py-2 rounded bg-[#5B2A4F] hover:bg-[#46203D] text-white text-xs tracking-[0.2em] uppercase font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {linkBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Send login link
              </button>
              {loginLink && (
                <div className="mt-2 bg-[#FBF6E9] border border-dashed border-[#C49247] rounded p-2.5" data-testid="profile-login-link">
                  <div className="text-[11px] text-[#3C2A1E] break-all font-mono">{loginLink.url}</div>
                  <div className="mt-1.5 flex items-center justify-between gap-2">
                    <span className="text-[11px] text-[#7A6A55]">
                      {loginLink.emailed ? "Emailed to them too." : "Email didn't send , copy it across yourself."}
                    </span>
                    <button
                      type="button"
                      data-testid="profile-login-link-copy"
                      onClick={() => { navigator.clipboard.writeText(loginLink.url); toast.success("Login link copied."); }}
                      className="shrink-0 text-[10px] tracking-[0.2em] uppercase font-semibold text-[#6E1E3A] inline-flex items-center gap-1 hover:underline"
                    >
                      <Copy className="w-3 h-3" /> Copy
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Orders FIRST , one list across every source */}
            <UnifiedOrdersPanel
              orders={profile?.orders || []}
              legacy={hqLegacy}
              invite={invite}
              customer={form}
              onRebuild={(kind, id, label) => rebuildOrder(kind, id, label)}
              rebuildBusy={rebuildBusy}
              onChanged={() => onSaved && onSaved()}
            />

            {/* HQ Portal invite */}
            <InvitePanel invite={invite} customer={form} />

            {/* HQ status, VIP access, password reset */}
            <HqAccessPanel profile={profile} customer={form} onProfileChange={() => {}} />

            {/* Heritage credits + vouchers */}
            <CreditsPanel hqAccount={profile?.hq_account} customer={form} />

            {/* Wishlist */}
            <section className="mt-6 bg-white border border-[#E8DFCB] rounded-md p-4" data-testid="profile-wishlist-panel">
              <div className="text-[10px] tracking-[0.35em] uppercase text-[#6E1E3A] font-semibold inline-flex items-center gap-1.5">
                <Flag className="w-3.5 h-3.5" /> Wishlist
              </div>
              {(profile?.wishlist || []).length === 0 ? (
                <p className="mt-2 text-xs text-[#7A6A55] italic">No wishlist saves yet.</p>
              ) : (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {profile.wishlist.map((w) => (
                    <span key={w.id} className="px-2.5 py-1 text-xs rounded-full bg-[#6E1E3A]/10 text-[#6E1E3A] border border-[#6E1E3A]/20" data-testid={`wishlist-chip-${w.id}`}>
                      ♥ {w.name}
                    </span>
                  ))}
                </div>
              )}
            </section>

            {/* Two-way messages with this customer */}
            <MessagesPanel customerId={form.id} customerName={form.name} hasEmail={!!form.email} />

          </>
        )}
      </aside>
    </div>
  );
}

function HqAccessPanel({ profile, customer }) {
  const c = profile?.customer || {};
  const [vip, setVip] = useState(c.vip_enabled !== false);
  const [busy, setBusy] = useState("");

  useEffect(() => { setVip((profile?.customer || {}).vip_enabled !== false); }, [profile]);

  const toggleVip = async () => {
    const next = !vip;
    setBusy("vip");
    try {
      await api.post(`/console/customers/${customer.id}/vip`, { enabled: next });
      setVip(next);
      toast.success(next ? "VIP Build-a-Box access ON." : "VIP Build-a-Box access OFF.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update VIP access.");
    } finally { setBusy(""); }
  };

  const resetPassword = async () => {
    if (!window.confirm("Reset this customer's HQ login password? They'll get a temporary password to log in with.")) return;
    setBusy("pwd");
    try {
      const r = await api.post(`/console/customers/${customer.id}/temp-password`);
      const pwd = r.data?.temp_password;
      try { await navigator.clipboard.writeText(pwd); } catch { /* clipboard blocked */ }
      window.alert(`Temporary password (copied to clipboard):\n\n${pwd}\n\nIt expires in 48 hours, the customer sets their own password on first login.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't reset password.");
    } finally { setBusy(""); }
  };

  const orders = profile?.orders || [];
  const commRows = [];
  if (profile?.invite_sent_at) commRows.push(`HQ invite emailed · ${new Date(profile.invite_sent_at).toLocaleDateString("en-NZ")}`);
  orders.forEach((o) => {
    const ref = `#${(o.id || "").slice(0, 8)}`;
    if (o.invoice_email_sent_at) commRows.push(`Invoice emailed (${ref}) · ${new Date(o.invoice_email_sent_at).toLocaleDateString("en-NZ")}`);
    if (o.reminder_last_sent_at) commRows.push(`Payment reminder${o.reminder_count > 1 ? ` ×${o.reminder_count}` : ""} (${ref}) · ${new Date(o.reminder_last_sent_at).toLocaleDateString("en-NZ")}`);
    if (o.tracking_email_sent_at) commRows.push(`Tracking emailed (${ref}) · ${new Date(o.tracking_email_sent_at).toLocaleDateString("en-NZ")}`);
  });
  (profile?.comms || []).forEach((c) => {
    commRows.push(`${c.note} · ${new Date(c.at).toLocaleDateString("en-NZ")}`);
  });

  return (
    <section className="mt-6 bg-white border border-[#E8DFCB] rounded-md p-4" data-testid="profile-hq-panel">
      <div className="text-[10px] tracking-[0.35em] uppercase text-[#6E1E3A] font-semibold inline-flex items-center gap-1.5">
        <Crown className="w-3.5 h-3.5" /> HQ &amp; VIP access
      </div>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#9C8B73]">HQ account</div>
          <div className="mt-0.5 font-semibold" data-testid="profile-hq-status">
            {profile?.hq_account ? <span className="text-green-800">✓ Joined Heritage HQ</span> :
              profile?.invite_sent_at ? <span className="text-[#C49247]">Invited · not joined yet</span> :
              <span className="text-[#A33030]">Not invited yet</span>}
          </div>
        </div>
        <div className="flex items-center justify-between sm:justify-end gap-3">
          <span className="text-[10px] tracking-[0.25em] uppercase text-[#9C8B73]">VIP Build-a-Box</span>
          <button
            type="button"
            onClick={toggleVip}
            disabled={busy === "vip"}
            data-testid="profile-vip-toggle"
            role="switch"
            aria-checked={vip}
            className={`relative w-12 h-6 rounded-full transition ${vip ? "bg-[#2F4A2A]" : "bg-[#C9BBA4]"}`}
            title={vip ? "VIP access is ON, click to turn off" : "VIP access is OFF, click to turn on"}
          >
            <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${vip ? "left-6" : "left-0.5"}`} />
          </button>
          <span className={`text-[10px] tracking-[0.2em] uppercase font-semibold ${vip ? "text-green-800" : "text-[#A33030]"}`} data-testid="profile-vip-state">{vip ? "On" : "Off"}</span>
        </div>
      </div>
      {profile?.hq_account && (
        <button
          type="button"
          onClick={resetPassword}
          disabled={busy === "pwd"}
          data-testid="profile-reset-password"
          className="mt-3 px-3 py-2 text-[10px] tracking-[0.2em] uppercase font-semibold bg-white border border-[#3C2A1E] hover:bg-[#FBF6E9] text-[#3C2A1E] rounded inline-flex items-center gap-1.5 disabled:opacity-60"
        >
          <Key className="w-3 h-3" /> Reset login password
        </button>
      )}
      <div className="mt-4 border-t border-[#F0E9D8] pt-3">
        <div className="text-[10px] tracking-[0.25em] uppercase text-[#9C8B73] font-semibold">Communications sent</div>
        {commRows.length === 0 ? (
          <p className="mt-1 text-xs text-[#7A6A55] italic" data-testid="profile-comms-empty">No communications logged yet.</p>
        ) : (
          <ul className="mt-1.5 space-y-1 text-xs text-[#3C2A1E]" data-testid="profile-comms-list">
            {commRows.map((row, i) => <li key={i}>· {row}</li>)}
          </ul>
        )}
      </div>
    </section>
  );
}

function CreditsPanel({ hqAccount, customer }) {
  const [balance, setBalance] = useState(null);
  const [vouchers, setVouchers] = useState([]);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    setBalance(hqAccount ? Number(hqAccount.heritage_credits || 0) : null);
    setVouchers(hqAccount?.vouchers || []);
  }, [hqAccount]);

  const email = (customer?.email || "").trim();

  const adjust = async () => {
    const raw = window.prompt("Credit change in NZ$ (positive to give, negative to remove). E.g. 25 or -10");
    if (raw === null) return;
    const delta = Number(raw);
    if (!Number.isFinite(delta) || delta === 0) { toast.error("Enter a non-zero number."); return; }
    const reason = window.prompt("Reason (optional)") || "";
    setBusy("credit");
    try {
      const r = await api.post(`/console/hq-customers/${encodeURIComponent(email)}/credits/adjust`, { delta, reason });
      setBalance(Number(r.data.heritage_credits));
      toast.success(`Credit ${delta > 0 ? "given" : "removed"}. New balance NZ$${Number(r.data.heritage_credits).toFixed(2)}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't adjust credit.");
    } finally { setBusy(""); }
  };

  const voucher = async () => {
    const raw = window.prompt("Voucher amount (NZ$). E.g. 50");
    if (raw === null) return;
    const amount = Number(raw);
    if (!Number.isFinite(amount) || amount <= 0) { toast.error("Enter a positive amount."); return; }
    const note = window.prompt("Voucher note (optional)") || "";
    setBusy("voucher");
    try {
      const r = await api.post(`/console/hq-customers/${encodeURIComponent(email)}/voucher`, { amount, note });
      setVouchers((v) => [...v, r.data]);
      toast.success(`Voucher created: ${r.data.code} (NZ$${amount}).`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create voucher.");
    } finally { setBusy(""); }
  };

  return (
    <section className="mt-6 bg-white border border-[#E8DFCB] rounded-md p-4" data-testid="profile-credits-panel">
      <div className="text-[10px] tracking-[0.35em] uppercase text-[#6E1E3A] font-semibold inline-flex items-center gap-1.5">
        <Wallet className="w-3.5 h-3.5" /> Heritage credits &amp; vouchers
      </div>
      {!hqAccount ? (
        <p className="mt-2 text-xs text-[#7A6A55]">
          Credits live in the customer's HQ account. This customer hasn't joined Heritage HQ yet ,
          send them the invite above first, then you can give them credit.
        </p>
      ) : (
        <>
          <div className="mt-3 flex items-end justify-between gap-3">
            <div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#9C8B73]">Balance</div>
              <div className="font-serif text-2xl text-[#3C2A1E]" data-testid="profile-credit-balance">
                NZ${Number(balance || 0).toFixed(2)}
              </div>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={adjust} disabled={busy === "credit"} data-testid="profile-credit-adjust"
                className="px-3 py-2 text-[11px] tracking-[0.2em] uppercase bg-[#3C2A1E] hover:bg-[#26190E] text-white rounded inline-flex items-center gap-1.5 disabled:opacity-60">
                {busy === "credit" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wallet className="w-3 h-3" />} Give credit
              </button>
              <button type="button" onClick={voucher} disabled={busy === "voucher"} data-testid="profile-voucher-create"
                className="px-3 py-2 text-[11px] tracking-[0.2em] uppercase bg-white border border-[#E8DFCB] hover:bg-[#FBF6E9] text-[#3C2A1E] rounded inline-flex items-center gap-1.5 disabled:opacity-60">
                {busy === "voucher" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Ticket className="w-3 h-3" />} Voucher
              </button>
            </div>
          </div>
          {vouchers.length > 0 && (
            <ul className="mt-3 space-y-1 text-xs text-[#3C2A1E]" data-testid="profile-voucher-list">
              {vouchers.map((v) => (
                <li key={v.id} className="flex items-center justify-between border-t border-[#F0E9D8] pt-1.5">
                  <span className="font-mono">{v.code}</span>
                  <span>NZ${Number(v.amount).toFixed(2)}{v.redeemed ? " · redeemed" : ""}</span>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-2 text-[11px] text-[#9C8B73]">1 credit = NZ$1, auto-applied at checkout.</p>
        </>
      )}
    </section>
  );
}

function MessagesPanel({ customerId, customerName, hasEmail }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (!customerId) return;
    api.get(`/console/customers/${customerId}/messages`)
      .then((r) => setMessages(r.data?.messages || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [customerId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [messages.length]);

  const send = async () => {
    const text = body.trim();
    if (!text) return;
    setSending(true);
    try {
      const r = await api.post(`/console/customers/${customerId}/messages`, { body: text });
      setMessages((m) => [...m, r.data]);
      setBody("");
      toast.success("Reply sent, the customer gets an email nudge too.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send.");
    } finally { setSending(false); }
  };

  return (
    <section className="mt-6 bg-white border border-[#E8DFCB] rounded-md p-4" data-testid="profile-messages-panel">
      <div className="text-[10px] tracking-[0.35em] uppercase text-[#6E1E3A] font-semibold inline-flex items-center gap-1.5">
        <MessageSquare className="w-3.5 h-3.5" /> Messages with {customerName?.split(" ")[0] || "customer"}
      </div>
      {!hasEmail ? (
        <p className="mt-2 text-xs text-[#7A6A55]">Add an email to this customer to start messaging.</p>
      ) : loading ? (
        <div className="mt-3 inline-flex items-center gap-2 text-xs text-[#7A6A55]"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…</div>
      ) : (
        <>
          {messages.length === 0 ? (
            <p className="mt-3 text-xs text-[#7A6A55] italic">No messages yet. Anything you send appears in their Heritage HQ, plus an email nudge.</p>
          ) : (
            <div className="mt-3 space-y-2 max-h-72 overflow-y-auto pr-1" data-testid="profile-messages-thread">
              {messages.map((m) => (
                <div key={m.id} className={`flex ${m.sender === "admin" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] rounded px-3 py-2 text-xs whitespace-pre-wrap ${
                      m.sender === "admin" ? "bg-[#6E1E3A] text-white" : "bg-[#FBF6E9] text-[#3C2A1E] border border-[#E8DFCB]"
                    }`}
                    data-testid={`admin-msg-${m.id}`}
                  >
                    <div className="text-[9px] tracking-[0.2em] uppercase font-semibold opacity-70 mb-0.5">
                      {m.sender === "admin" ? "You" : customerName?.split(" ")[0] || "Customer"} · {m.created_at ? new Date(m.created_at).toLocaleDateString("en-NZ") : ""}
                    </div>
                    {m.body}
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>
          )}
          <div className="mt-3 flex gap-2 items-end">
            <textarea
              rows={2}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Reply to this customer…"
              data-testid="profile-message-input"
              className="flex-1 px-3 py-2 text-xs border border-[#E8DFCB] rounded resize-none"
            />
            <button
              type="button"
              onClick={send}
              disabled={sending || !body.trim()}
              data-testid="profile-message-send"
              className="px-3 py-2 text-[10px] tracking-[0.2em] uppercase font-semibold bg-[#6E1E3A] hover:bg-[#5A1830] text-white rounded inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Send
            </button>
          </div>
        </>
      )}
    </section>
  );
}

function NewCustomerDrawer({ onClose, onCreated }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", address: "", notes: "", admin_notes: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.name.trim() && !f.email.trim()) { toast.error("Need a name or email."); return; }
    setBusy(true);
    try {
      const r = await api.post("/console/customers", f);
      toast.success("Customer added.");
      onCreated(r.data);
    } catch { toast.error("Couldn't save."); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-40 flex justify-end" data-testid="new-customer-drawer">
      <button type="button" onClick={onClose} className="absolute inset-0 bg-black/30" />
      <aside className="relative w-full max-w-md bg-[#F8F4EA] h-full overflow-y-auto p-6 shadow-2xl">
        <button type="button" onClick={onClose} className="absolute top-3 right-3 p-1.5 hover:bg-[#E8DFCB] rounded">
          <X className="w-4 h-4" />
        </button>
        <div className="text-[10px] tracking-[0.35em] uppercase text-[#6E1E3A] font-semibold">New customer</div>
        <h2 className="font-serif text-3xl text-[#3C2A1E] mt-1">Add to the database</h2>
        <div className="mt-5 space-y-3">
          <FieldRow label="Name"><Inp v={f.name} on={(v) => setF({ ...f, name: v })} tid="new-name" /></FieldRow>
          <FieldRow label="Email"><Inp v={f.email} on={(v) => setF({ ...f, email: v })} tid="new-email" /></FieldRow>
          <FieldRow label="Phone"><Inp v={f.phone} on={(v) => setF({ ...f, phone: v })} tid="new-phone" /></FieldRow>
          <FieldRow label="Address"><Inp v={f.address} on={(v) => setF({ ...f, address: v })} tid="new-address" /></FieldRow>
          <FieldRow label="Notes"><Txt v={f.notes} on={(v) => setF({ ...f, notes: v })} tid="new-notes" /></FieldRow>
          <FieldRow label="Admin notes"><Txt v={f.admin_notes} on={(v) => setF({ ...f, admin_notes: v })} tid="new-admin-notes" /></FieldRow>
        </div>
        <button type="button" onClick={submit} disabled={busy} data-testid="new-save" className="mt-5 w-full px-4 py-2.5 bg-[#3C2A1E] hover:bg-[#26190E] text-white rounded text-xs tracking-[0.25em] uppercase font-semibold disabled:opacity-60">
          {busy ? "Saving…" : "Save"}
        </button>
      </aside>
    </div>
  );
}

function FieldRow({ label, children }) {
  return (
    <label className="block text-[10px] tracking-[0.25em] uppercase text-[#9C8B73] font-semibold">
      {label}<div className="mt-1 font-normal normal-case tracking-normal">{children}</div>
    </label>
  );
}

function Inp({ v, on, tid }) {
  return <input type="text" value={v || ""} onChange={(e) => on(e.target.value)} data-testid={tid} className="w-full px-3 py-2 bg-white border border-[#E8DFCB] rounded text-sm text-[#3C2A1E] focus:outline-none focus:border-[#6E1E3A]" />;
}

function Txt({ v, on, tid }) {
  return <textarea rows={3} value={v || ""} onChange={(e) => on(e.target.value)} data-testid={tid} className="w-full px-3 py-2 bg-white border border-[#E8DFCB] rounded text-sm text-[#3C2A1E] leading-relaxed focus:outline-none focus:border-[#6E1E3A]" />;
}

function LegacyGroup({ title, items, onRebuild, busy }) {
  return (
    <div className="mb-3">
      <div className="text-[10px] tracking-[0.3em] uppercase text-[#9C8B73] font-semibold mb-1.5">{title}</div>
      {items.map((it) => (
        <div key={it.id} className="bg-white border border-[#E8DFCB] rounded p-2.5 mb-1.5 flex items-start justify-between gap-2" data-testid={`legacy-${it.id}`}>
          <div className="min-w-0 flex-1">
            <div className="text-sm text-[#3C2A1E] truncate">{it.label}</div>
            <div className="text-[11px] text-[#7A6A55] truncate">{it.sub}</div>
            <div className="text-[10px] text-[#9C8B73] tracking-[0.2em] uppercase mt-0.5">
              {new Date(it.date).toLocaleDateString("en-NZ")}
            </div>
          </div>
          <button
            type="button"
            onClick={() => onRebuild(it.id, it.label)}
            disabled={busy === it.id}
            data-testid={`rebuild-${it.id}`}
            className="px-2.5 py-1 text-[10px] tracking-[0.2em] uppercase bg-[#6E1E3A] hover:bg-[#5a1830] text-white rounded whitespace-nowrap"
          >
            Rebuild →
          </button>
        </div>
      ))}
    </div>
  );
}


/* ─────────────────────────────────────────────────────────────────────────
 * Heritage HQ , Portal invite panel inside the customer drawer.
 * Shows the ready signup URL, the full text invite to copy, and a one-click
 * "Send invite via Resend" button that calls /admin/customers/invite-portal.
 * Hides itself entirely if the customer already has an HQ account.
 * ─────────────────────────────────────────────────────────────────────── */
function InvitePanel({ invite, customer }) {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!invite || !customer) return null;

  if (invite.in_hq) {
    return (
      <div className="mt-5 p-3 rounded border border-[#C8DCB6] bg-[#EBF3E5] text-xs text-[#2F4A2A] flex items-center gap-2" data-testid="invite-already-in-hq">
        <CheckCircle2 className="w-4 h-4" />
        <span><strong>{customer.name || customer.email}</strong> is already a Heritage Headquarters customer.</span>
      </div>
    );
  }

  if (!invite.email) {
    return (
      <div className="mt-5 p-3 rounded border border-[#E8B5A2] bg-[#FFF4F0] text-xs text-[#A33030]" data-testid="invite-no-email">
        Add an email address above before sending a Heritage Headquarters invite.
      </div>
    );
  }

  const send = async () => {
    setSending(true);
    try {
      const r = await api.post("/admin/customers/invite-portal", {
        email: invite.email,
        name: customer.name || "",
      });
      if (r.data?.ok) {
        setSent(true);
        toast.success(`Invite emailed to ${invite.email}.`);
      } else {
        toast.error(r.data?.error || "The invite email couldn't be sent, please try again.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send the invite.");
    } finally { setSending(false); }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(invite.signup_url);
      setCopied(true);
      toast.success("Signup link copied.");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy the link.");
    }
  };

  return (
    <div className="mt-6 border rounded-md overflow-hidden" data-testid="invite-panel" style={{ borderColor: "#E0D1C8" }}>
      <div className="px-4 py-2.5" style={{ background: "#3E1F46" }}>
        <div className="text-[9px] tracking-[0.4em] uppercase font-semibold" style={{ color: "#C9A268" }}>
          ✦ Heritage Headquarters
        </div>
        <div className="text-base font-serif text-[#FBF6E9] leading-tight">Invite to the customer portal</div>
      </div>
      <div className="p-4 bg-white">
        <p className="text-xs text-[#7A6A55] leading-relaxed">
          One click sends {customer.name?.split(" ")[0] || "them"} a friendly email with their personal
          signup link, their past orders connect automatically once they join.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={send}
            disabled={sending || sent}
            data-testid="invite-send-resend"
            className="flex-1 min-w-[150px] px-4 py-2.5 bg-[#2F4A2A] hover:bg-[#243A20] text-white rounded text-[10px] tracking-[0.25em] uppercase font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-60"
          >
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : sent ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Send className="w-3.5 h-3.5" />}
            {sending ? "Sending…" : sent ? "Invite sent" : "Email HQ invite"}
          </button>
          <button
            type="button"
            onClick={copyLink}
            data-testid="invite-copy-link"
            className="px-4 py-2.5 bg-white border border-[#3C2A1E] hover:bg-[#FBF6E9] text-[#3C2A1E] rounded text-[10px] tracking-[0.25em] uppercase font-semibold inline-flex items-center justify-center gap-1.5"
          >
            {copied ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            {copied ? "Copied" : "Copy signup link"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
 * Unified Orders Panel , ONE list. Every order tied to this customer,
 * regardless of source (console, unconfirmed imports, legacy manual /
 * VIP / mystery boxes). Sorted by date desc. Confirm / Send-to-Historical
 * / Send-Invite actions are inline per row when applicable.
 * ────────────────────────────────────────────────────────────────────── */
function UnifiedOrdersPanel({ orders = [], legacy, invite, customer, onRebuild, rebuildBusy, onChanged }) {
  const [busyId, setBusyId] = useState(null);
  const [sendingInvite, setSendingInvite] = useState(false);

  // Product/order name first (order ref moves to the meta line below).
  const consoleLabel = (o) => {
    if (o.kind === "mothers_day") return "Mother's Day Box";
    if (o.kind === "mystery_box") return `Mystery Box${o.mystery_tier ? " · " + o.mystery_tier : ""}`;
    if (o.kind === "tuber_shop") return "Tuber Order";
    const items = o.line_items || [];
    if (items.some((i) => i.source === "fairsale")) return `Fairitage Sale (${items.length} Tuber${items.length === 1 ? "" : "s"})`;
    if (items.length === 1) {
      const it = items[0];
      const qty = Number(it.qty || 1);
      return qty > 1 ? `${it.name} ×${qty}` : it.name || "Console Order";
    }
    if (items.length > 1) return `${items[0].name} +${items.length - 1} more`;
    return "Console Order";
  };

  // Normalise every order source into one shape.
  const rows = [];
  (orders || []).forEach((o) => {
    const isUnconfirmed = o.review_status === "awaiting_review";
    rows.push({
      id: o.id,
      source: "console",
      label: consoleLabel(o),
      ref: o.order_number ? `#${o.order_number}` : `#${(o.id || "").slice(0, 8)}`,
      total: Number(o.total || 0),
      created_at: o.created_at,
      stage: o.stage,
      review_status: o.review_status || "confirmed",
      unconfirmed: isUnconfirmed,
      payment_status: o.payment_status,
      notes: o.notes,
      items: o.line_items,
      raw: o,
    });
  });
  (legacy?.manual_orders || []).forEach((m) => rows.push({
    id: m.id, source: "manual", label: (m.items || "").split("\n")[0]?.slice(0, 40) || "Manual Order",
    ref: "Manual", total: Number(m.amount || 0), created_at: m.created_at, stage: m.status || "manual",
    review_status: "legacy", legacy: true,
  }));
  (legacy?.vip_boxes || []).forEach((b) => rows.push({
    id: b.id, source: "vip", label: `${b.size}-pack VIP box`, ref: `#${(b.id || "").slice(0, 8)}`,
    total: Number(b.price || 0), created_at: b.created_at,
    stage: `${b.filled || 0}/${b.size} filled`, review_status: "legacy", legacy: true,
  }));
  (legacy?.mystery_boxes || []).forEach((m) => rows.push({
    id: m.id, source: "mystery", label: m.label || "Mystery box", ref: `#${(m.id || "").slice(0, 8)}`,
    total: Number(m.price || 0), created_at: m.created_at, stage: m.status || "mystery",
    review_status: "legacy", legacy: true,
  }));
  rows.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));

  const confirmedCount = rows.filter((r) => r.review_status === "confirmed").length;
  const canInvite = !!invite?.email && !invite?.in_hq && confirmedCount > 0;

  const confirmOrder = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/admin/imported-orders/${id}/confirm`);
      toast.success("Order confirmed.");
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't confirm.");
    } finally { setBusyId(null); }
  };
  const moveToHistorical = async (id) => {
    if (!window.confirm("Move to Historical Orders? It will leave the active pipeline.")) return;
    setBusyId(id);
    try {
      await api.post(`/admin/imported-orders/${id}/move-to-historical`);
      toast.success("Filed under Historical Orders.");
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't move.");
    } finally { setBusyId(null); }
  };
  const sendReminder = async (id) => {
    setBusyId(id);
    try {
      const r = await api.post(`/console/orders/${id}/email-reminder`, { origin_url: window.location.origin });
      toast.success(`Payment reminder emailed to ${r.data.sent_to}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send reminder.");
    } finally { setBusyId(null); }
  };

  const sendInvite = async () => {
    if (!canInvite) return;
    if (!window.confirm(`Send the Heritage Headquarters invite to ${invite.email} via Resend now?`)) return;
    setSendingInvite(true);
    try {
      const r = await api.post("/admin/customers/invite-portal", { email: invite.email, name: customer?.name || "" });
      if (r.data?.ok) toast.success(`Invite emailed to ${invite.email}.`);
      else toast.error(r.data?.error || "Resend rejected the send.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send invite.");
    } finally { setSendingInvite(false); }
  };

  return (
    <div className="mt-7" data-testid="unified-orders-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] tracking-[0.35em] uppercase text-[#6E1E3A] font-semibold">
          ✦ Orders on file ({rows.length})
        </div>
        {invite?.in_hq ? (
          <span className="text-[10px] tracking-[0.25em] uppercase text-[#2F4A2A] font-semibold inline-flex items-center gap-1" data-testid="unified-invite-in-hq">
            <CheckCircle2 className="w-3 h-3" /> In HQ
          </span>
        ) : (
          <button
            type="button"
            onClick={sendInvite}
            disabled={!canInvite || sendingInvite}
            data-testid="unified-send-invite"
            title={canInvite ? "Send HQ invite via Resend" : "Add an email + confirm at least one order first"}
            className="px-2.5 py-1.5 text-[10px] tracking-[0.2em] uppercase font-semibold rounded inline-flex items-center gap-1 disabled:opacity-40"
            style={{ backgroundColor: "#3E1F46", color: "white" }}
          >
            {sendingInvite ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
            Send Invite
          </button>
        )}
      </div>

      {rows.length === 0 ? (
        <div className="text-xs text-[#7A6A55] italic bg-white border border-dashed border-[#E8DFCB] rounded p-4 text-center">
          No orders on file yet. Add a fresh order from the Create Order button.
        </div>
      ) : rows.map((r) => (
        <article
          key={`${r.source}-${r.id}`}
          data-testid={`unified-order-${r.id}`}
          className="bg-white border rounded p-3 mb-2 text-xs"
          style={{ borderColor: r.unconfirmed ? "#E8B5A2" : "#E8DFCB" }}
        >
          <div className="flex justify-between items-start gap-2">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-[#3C2A1E]">{r.label}</span>
                <span className="text-[9px] tracking-[0.2em] uppercase font-semibold px-1.5 py-0.5 rounded shrink-0"
                  style={
                    r.unconfirmed ? { backgroundColor: "#FFF4F0", color: "#A33030" }
                    : r.legacy ? { backgroundColor: "#F4ECD8", color: "#7A6A55" }
                    : { backgroundColor: "#6E1E3A20", color: "#6E1E3A" }
                  }
                >
                  {r.unconfirmed ? "Unconfirmed" : r.legacy ? "Legacy" : r.stage || "Active"}
                </span>
                {r.payment_status === "paid" && (
                  <span className="text-[9px] tracking-[0.2em] uppercase font-semibold px-1.5 py-0.5 rounded" style={{ backgroundColor: "#2F4A2A", color: "white" }}>Paid</span>
                )}
              </div>
              <div className="text-[#7A6A55] mt-0.5">
                {r.ref ? <span className="opacity-75">{r.ref} · </span> : null}
                {r.created_at ? new Date(r.created_at).toLocaleDateString("en-NZ") : "-"} · <strong>${r.total.toFixed(2)}</strong>
                {r.items?.length ? ` · ${r.items.length} item${r.items.length === 1 ? "" : "s"}` : ""}
              </div>
              {r.items?.length ? (
                <div className="text-[11px] text-[#3C2A1E] mt-1" data-testid={`unified-items-${r.id}`}>
                  {r.items.slice(0, 6).map((it, i) => <span key={i} className="inline-block mr-2 px-1.5 py-0.5 rounded bg-[#FBF6E9] border border-[#E8DFCB]">{it.qty || 1}× {it.name}</span>)}
                  {r.items.length > 6 && <span className="opacity-60">+{r.items.length - 6} more</span>}
                </div>
              ) : null}
              {r.notes && <div className="text-[10px] text-[#7A6A55] italic mt-1"><strong>Note:</strong> {r.notes}</div>}
            </div>
          </div>

          <div className="flex flex-wrap gap-1.5 mt-2">
            {r.source === "console" && !r.unconfirmed && (
              <>
                <Link
                  to={`/admin/order/${r.id}`}
                  data-testid={`unified-open-${r.id}`}
                  className="px-2.5 py-1.5 text-[10px] tracking-[0.2em] uppercase font-semibold rounded border inline-flex items-center gap-1"
                  style={{ borderColor: "#3C2A1E", color: "#3C2A1E" }}
                >
                  <Eye className="w-3 h-3" /> Open order
                </Link>
                {r.stage === "awaiting_payment" && r.payment_status !== "paid" && customer?.email && (
                  <button
                    type="button"
                    onClick={() => sendReminder(r.id)}
                    disabled={busyId === r.id}
                    data-testid={`unified-remind-${r.id}`}
                    title="Email a payment reminder with pay link"
                    className="px-2.5 py-1.5 text-[10px] tracking-[0.2em] uppercase font-semibold rounded inline-flex items-center gap-1 disabled:opacity-50"
                    style={{ backgroundColor: "#C49247", color: "white" }}
                  >
                    {busyId === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <BellRing className="w-3 h-3" />} Send reminder
                  </button>
                )}
              </>
            )}
            {r.unconfirmed && (
              <>
                <a
                  href={`/console/orders/awaiting_payment#${r.id}`}
                  data-testid={`unified-edit-${r.id}`}
                  className="px-2.5 py-1.5 text-[10px] tracking-[0.2em] uppercase font-semibold rounded border inline-flex items-center gap-1"
                  style={{ borderColor: "#3C2A1E", color: "#3C2A1E" }}
                >
                  <Edit className="w-3 h-3" /> Edit
                </a>
                <button
                  type="button"
                  onClick={() => confirmOrder(r.id)}
                  disabled={busyId === r.id}
                  data-testid={`unified-confirm-${r.id}`}
                  className="px-2.5 py-1.5 text-[10px] tracking-[0.2em] uppercase font-semibold rounded inline-flex items-center gap-1 disabled:opacity-50"
                  style={{ backgroundColor: "#2F4A2A", color: "white" }}
                >
                  {busyId === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} Confirm
                </button>
                <button
                  type="button"
                  onClick={() => moveToHistorical(r.id)}
                  disabled={busyId === r.id}
                  data-testid={`unified-historical-${r.id}`}
                  className="px-2.5 py-1.5 text-[10px] tracking-[0.2em] uppercase font-semibold rounded border inline-flex items-center gap-1 disabled:opacity-50"
                  style={{ borderColor: "#7A6A55", color: "#7A6A55" }}
                >
                  <Archive className="w-3 h-3" /> Historical
                </button>
              </>
            )}
            {r.legacy && (
              <button
                type="button"
                onClick={() => onRebuild(r.source, r.id, r.label)}
                disabled={rebuildBusy === `${r.source}:${r.id}`}
                data-testid={`unified-rebuild-${r.id}`}
                className="px-2.5 py-1.5 text-[10px] tracking-[0.2em] uppercase font-semibold rounded border inline-flex items-center gap-1"
                style={{ borderColor: "#3C2A1E", color: "#3C2A1E" }}
              >
                {rebuildBusy === `${r.source}:${r.id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : "↻"} Rebuild as fresh order
              </button>
            )}
          </div>
        </article>
      ))}
    </div>
  );
}
